/**
 * Pure geometry functions extracted from EditHandleManager.
 *
 * Every function here operates on plain data (coordinate arrays, numbers, etc.)
 * and has **no** dependency on maplibre-gl, the DOM, or any service singletons.
 */

import { distance as turfDistance, destination as turfDestination } from '@turf/turf'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Longitude, Latitude pair */
export type LngLatTuple = [number, number]

/** Coordinate arrays vary by geometry type; narrowed via feature.type in each function. */
type CoordinateArray = number[] | number[][] | number[][][]

export interface FeatureGeometry {
  type: string
  coordinates: CoordinateArray
}

export interface FeatureLike {
  type: string
  geometry: FeatureGeometry
  style?: { radius?: number; [key: string]: unknown }
  properties?: { radius?: number; [key: string]: unknown }
}

// ---------------------------------------------------------------------------
// computeHandlePositions
// ---------------------------------------------------------------------------

/**
 * Return the handle positions for a feature.
 *
 * The order matches the marker creation order in EditHandleManager so the
 * `updateHandlePositions` fast-path can zip markers with positions.
 */
export function computeHandlePositions(feature: FeatureLike): LngLatTuple[] {
  const positions: LngLatTuple[] = []

  switch (feature.type) {
    case 'point': {
      const c = feature.geometry.coordinates as number[]
      positions.push([c[0], c[1]])
      break
    }
    case 'line': {
      const coords = feature.geometry.coordinates as number[][]
      coords.forEach((coord, i) => {
        positions.push([coord[0], coord[1]])
        if (i < coords.length - 1) {
          const next = coords[i + 1]
          positions.push([(coord[0] + next[0]) / 2, (coord[1] + next[1]) / 2])
        }
      })
      break
    }
    case 'polygon': {
      const rings = feature.geometry.coordinates as number[][][]
      const verts: number[][] = rings[0].slice(0, -1)
      verts.forEach((coord, i) => {
        positions.push([coord[0], coord[1]])
        const next = verts[(i + 1) % verts.length]
        positions.push([(coord[0] + next[0]) / 2, (coord[1] + next[1]) / 2])
      })
      break
    }
    case 'circle': {
      const c = feature.geometry.coordinates as number[]
      const radius = feature.style?.radius ?? feature.properties?.radius ?? 100
      const dest = turfDestination([c[0], c[1]], radius, 90, { units: 'meters' })
      const [rLng, rLat] = dest.geometry.coordinates
      positions.push([rLng, rLat])
      positions.push([c[0], c[1]])
      break
    }
    case 'rectangle': {
      const rings = feature.geometry.coordinates as number[][][]
      const corners: number[][] = rings[0].slice(0, -1)
      corners.forEach(coord => {
        positions.push([coord[0], coord[1]])
      })
      const centerLng = corners.reduce((s, c) => s + c[0], 0) / corners.length
      const centerLat = corners.reduce((s, c) => s + c[1], 0) / corners.length
      positions.push([centerLng, centerLat])
      break
    }
  }

  return positions
}

// ---------------------------------------------------------------------------
// previewMoveFeature
// ---------------------------------------------------------------------------

/**
 * Compute the new geometry when dragging (moving) a whole feature.
 *
 * @param feature       The *original* (pre-drag) feature.
 * @param newPosition   The current drag position [lng, lat].
 * @param dragStart     The position where the drag started [lng, lat].
 *                      Required for line and polygon types.
 */
export function computeMoveFeature(
  feature: FeatureLike,
  newPosition: LngLatTuple,
  dragStart?: LngLatTuple
): FeatureGeometry {
  const geometry = feature.geometry
  const [newLng, newLat] = newPosition

  if (feature.type === 'point' || feature.type === 'circle') {
    return { ...geometry, coordinates: [newLng, newLat] }
  }

  if (feature.type === 'rectangle') {
    const rings = geometry.coordinates as number[][][]
    const corners: number[][] = rings[0].slice(0, -1)
    const currentCenterLng = corners.reduce((s, c) => s + c[0], 0) / corners.length
    const currentCenterLat = corners.reduce((s, c) => s + c[1], 0) / corners.length
    const offsetLng = newLng - currentCenterLng
    const offsetLat = newLat - currentCenterLat
    const newCorners = corners.map(c => [c[0] + offsetLng, c[1] + offsetLat])
    return { type: 'Polygon', coordinates: [[...newCorners, newCorners[0]]] }
  }

  if (feature.type === 'line') {
    const [startLng, startLat] = dragStart!
    const offsetLng = newLng - startLng
    const offsetLat = newLat - startLat
    const coords = geometry.coordinates as number[][]
    const newCoords = coords.map(c => [c[0] + offsetLng, c[1] + offsetLat])
    return { ...geometry, coordinates: newCoords }
  }

  if (feature.type === 'polygon') {
    const [startLng, startLat] = dragStart!
    const offsetLng = newLng - startLng
    const offsetLat = newLat - startLat
    const rings = geometry.coordinates as number[][][]
    const newCoords = rings[0].map(c => [c[0] + offsetLng, c[1] + offsetLat])
    return { type: 'Polygon', coordinates: [newCoords] }
  }

  return geometry
}

// ---------------------------------------------------------------------------
// previewMoveVertex
// ---------------------------------------------------------------------------

/**
 * Compute the new geometry when dragging a single vertex.
 */
export function computeMoveVertex(
  feature: FeatureLike,
  vertexIndex: number,
  newPosition: LngLatTuple
): FeatureGeometry {
  const geometry = feature.geometry
  const [newLng, newLat] = newPosition

  if (feature.type === 'line') {
    const coords = geometry.coordinates as number[][]
    const newCoords = coords.map((coord, i) => (i === vertexIndex ? [newLng, newLat] : [...coord]))
    return { ...geometry, coordinates: newCoords }
  }

  if (feature.type === 'polygon') {
    const rings = geometry.coordinates as number[][][]
    const newCoords = rings[0].map((coord, i) =>
      i === vertexIndex ? [newLng, newLat] : [...coord]
    )
    return { type: 'Polygon', coordinates: [newCoords] }
  }

  return geometry
}

// ---------------------------------------------------------------------------
// previewResizeCircle
// ---------------------------------------------------------------------------

/**
 * Compute the new radius when dragging a circle's edge handle.
 *
 * Returns the new radius in metres.
 */
export function computeResizeCircle(
  centerLng: number,
  centerLat: number,
  radiusLng: number,
  radiusLat: number
): number {
  return turfDistance([centerLng, centerLat], [radiusLng, radiusLat], { units: 'meters' })
}

// ---------------------------------------------------------------------------
// previewResizeRectangleCorner
// ---------------------------------------------------------------------------

/**
 * Compute the new rectangle geometry when dragging a corner.
 *
 * The opposite corner stays fixed; the two adjacent corners adjust to keep
 * the shape axis-aligned.
 *
 * @returns Closed polygon coordinates `[[c0, c1, c2, c3, c0]]`
 */
export function computeResizeRectangleCorner(
  corners: number[][],
  cornerIndex: number,
  newPosition: LngLatTuple
): number[][] {
  const [newLng, newLat] = newPosition
  const newCorners = corners.map(c => [...c])
  newCorners[cornerIndex] = [newLng, newLat]

  if (corners.length === 4) {
    const oppositeIndex = (cornerIndex + 2) % 4
    const oppositeCorner = corners[oppositeIndex]
    const prevIndex = (cornerIndex + 3) % 4
    const nextIndex = (cornerIndex + 1) % 4
    newCorners[prevIndex] = [oppositeCorner[0], newLat]
    newCorners[nextIndex] = [newLng, oppositeCorner[1]]
  }

  return [...newCorners, newCorners[0]]
}
